import { useState, useEffect, useCallback } from "react"
import { X, ZoomIn, ChevronLeft, ChevronRight } from "lucide-react"

export type GalleryImage = {
  id: number
  url: string | null
  thumb_url: string | null
  alt_text: string
  caption: string | null
  credit?: string | null
  credit_url?: string | null
  is_primary: boolean
}

type Props = {
  images: GalleryImage[]
  emptyIcon?: React.ReactNode
}

export function Lightbox({
  images,
  startIndex,
  onClose,
}: {
  images: GalleryImage[]
  startIndex: number
  onClose: () => void
}) {
  const [ index, setIndex ] = useState(startIndex)

  const prev = useCallback(() => setIndex((i) => (i - 1 + images.length) % images.length), [ images.length ])
  const next = useCallback(() => setIndex((i) => (i + 1) % images.length), [ images.length ])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
      if (e.key === "ArrowLeft") prev()
      if (e.key === "ArrowRight") next()
    }
    document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [ onClose, prev, next ])

  const current = images[index]

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
      onClick={onClose}
    >
      <button
        className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        onClick={onClose}
        aria-label="Close"
      >
        <X className="w-5 h-5" />
      </button>

      {images.length > 1 && (
        <>
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            onClick={(e) => { e.stopPropagation(); prev() }}
            aria-label="Previous image"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            onClick={(e) => { e.stopPropagation(); next() }}
            aria-label="Next image"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-sm font-mono">
            {index + 1} / {images.length}
          </div>
        </>
      )}

      <div className="flex flex-col items-center gap-3 max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
        {current.url && (
          <img
            src={current.url}
            alt={current.alt_text}
            className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
          />
        )}
        {current.caption && (
          <p className="text-white/80 text-sm text-center max-w-xl px-4">{current.caption}</p>
        )}
        {current.credit && (
          <p className="text-white/50 text-xs text-center max-w-xl px-4">
            {current.credit_url ? (
              <a
                href={current.credit_url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="hover:text-white/80 hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {current.credit}
              </a>
            ) : (
              current.credit
            )}
          </p>
        )}
      </div>
    </div>
  )
}

export function ImageGallery({ images, emptyIcon }: Props) {
  const [ lightboxIndex, setLightboxIndex ] = useState<number | null>(null)
  const openLightbox = useCallback((i: number) => setLightboxIndex(i), [])
  const closeLightbox = useCallback(() => setLightboxIndex(null), [])

  const primaryIndex = images.findIndex((img) => img.is_primary)
  const heroImage = primaryIndex >= 0 ? images[primaryIndex] : images[0] ?? null
  const heroIndex = primaryIndex >= 0 ? primaryIndex : 0

  return (
    <>
      {lightboxIndex !== null && images.length > 0 && (
        <Lightbox images={images} startIndex={lightboxIndex} onClose={closeLightbox} />
      )}

      <div className="w-full sm:w-[280px] shrink-0">
        <div className="aspect-square bg-stone-100 dark:bg-stone-800 rounded-xl overflow-hidden relative group/image">
          {heroImage?.url ? (
            <>
              <img
                src={heroImage.url}
                alt={heroImage.alt_text}
                className="w-full h-full object-cover cursor-zoom-in"
                onClick={() => openLightbox(heroIndex)}
              />
              <div className="absolute inset-0 bg-black/0 group-hover/image:bg-black/30 transition-colors flex items-start justify-end p-2 opacity-0 group-hover/image:opacity-100 transition-opacity pointer-events-none">
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-black/40">
                  <ZoomIn className="w-3.5 h-3.5 text-white" />
                </div>
              </div>
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              {emptyIcon}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
